/**
 * WeaveIn.ai Platform - Members API Service
 * TypeScript service layer for member management with custom fields
 */

import axios, { AxiosInstance } from 'axios';
import { applyUiLanguageHeader } from '@/services/uiLanguageHeader';
import { API_VERSION } from './organizations';
import { initializeActiveCboId } from '../../utils/auth';
import { offlineGet, isCurrentlyOnline } from '../offlineApiClient';
import type { ReassignRules } from '@/utils/reassignment';

import { config as productConfig } from '@/src/config';
// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export type FieldType = 'str' | 'long_text' | 'int' | 'bool' | 'datetime' | 'date' | 'time' | 'fixed_date' | 'select' | 'multi_select' | 'checkbox' | 'radio' | 'toggle';

export interface FieldOption {
  value: string;
  label: string;
}

export type VisibilityOperator = 'equals' | 'not_equals' | 'is_populated' | 'is_empty' | 'contains' | 'in';

export interface VisibilityCondition {
  field: string;
  operator: VisibilityOperator;
  value?: string;
}

export interface VisibilityRules {
  conditions: VisibilityCondition[];
  logic: 'AND' | 'OR';
  when_hidden: 'optional' | 'skip';
}

export interface CustomFieldValue {
  field_name: string;
  value: any;
}

export interface MemberBase {
  email?: string;
  phone_no?: string;
  first_name?: string;
  last_name?: string;
  race?: string;
  ethnicity?: string;
  language?: string;
  /** TRUS-738: where Preferred Language is saved, shown and auto-translated from. */
  preferred_language?: string;
  dob?: string;
  gender?: string;
  patient_no?: string;
  custom_fields?: CustomFieldValue[];
}

export interface MemberCreate extends MemberBase {
  first_name: string;
  last_name: string;
}

export interface Member {
  id: number;
  member_number?: string;
  email?: string;
  phone_no?: string;
  phone_number?: string;
  first_name?: string;
  last_name?: string;
  race?: string;
  ethnicity?: string;
  language?: string;
  preferred_language?: string;
  dob?: string;
  date_of_birth?: string;
  gender?: string;
  patient_no?: string;
  medicaid_id?: string;
  medicare_id?: string;
  commercial_insurance_id?: string;
  street_address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
  data_quality_score?: number;
  created_ts?: string;
  created_at?: string;
  last_updated_ts?: string;
  updated_at?: string;
  custom_fields?: Record<string, any>;
  // True when a member_workforce_profile row exists for this member.
  // Drives conditional rendering of the Workforce tab on the member page.
  is_workforce?: boolean;
  // This org's membership link status. 'inactive' means the member was soft-deleted
  // (archived) — the record and all its data still exist and can be restored.
  membership_status?: 'active' | 'inactive' | string | null;
  // 'direct'           — member belongs to the active org directly.
  // 'program_network'  — member is reached because the active org is a hub
  //                      and the member is enrolled in a hub-owned program
  //                      through a participating org.
  membership_source?: 'direct' | 'program_network' | null;
  // Org id where the member's home record lives. Useful for hub UIs that
  // want to surface "via Harmony Health" attribution alongside the name.
  home_cbo_id?: number | null;
  // Data Shield metadata (from backend DataShieldService)
  _shielded?: boolean;
  _shielded_fields?: string[];
  _shield_source?: Record<string, any>;
  _reveal_required?: boolean;
  _revealed?: boolean;
  // Effective access level for the current user on this member.
  // 'full' — can read and edit; 'view_only' — read-only.
  // Populated by the backend per request; staff users who are not assigned to
  // this member but can see them via program access receive 'view_only'.
  access_level?: 'full' | 'view_only';
}

export interface MemberListResponse {
  members: Member[];
  total: number;
  page: number;
  page_size: number;
}

export interface MemberLookupResponse {
  found: boolean;
  member?: Member;
  organizations: number[];
}

export interface Referral {
  id: number;
  member_id?: number;
  program_id?: number;
  cbo_id?: number;
  first_name?: string;
  last_name?: string;
  phone_number?: string;
  email?: string;
  medicaid_id?: string;
  consent_status: string;
  priority?: string;
  referral_source?: string;
  reason_for_referral?: string;
  needs_summary?: string;
  created_at: string;
  updated_at: string;
}

export interface Enrollment {
  id: number;
  member_id: number;
  program_id: number;
  cbo_id: number;
  status: string;
  enrollment_date?: string;
  end_date?: string;
  created_at: string;
  updated_at: string;
  program_name?: string;
}

export interface DataConflict {
  id: number;
  member_id: number;
  field_name: string;
  global_value?: string;
  org_value?: string;
  cbo_id: number;
  confidence_score: number;
  resolved: boolean;
  created_at: string;
  updated_at: string;
}

// ---------------------------------------------------------------------------
// Calculated / rule-based fields (TRUS-383 Phase 2)
//
// A field is DERIVED because it carries a rule, not because of its `type` —
// `type` still describes the value (int for an age, etc). The rule lives in
// `computed_formula`, the same column the Forms computed-field engine uses; a
// rule with no `scope` is same-record, which is what every legacy Forms formula
// already is. See app/services/derived_fields.py for the authoritative shape.
// ---------------------------------------------------------------------------

/** What the calculation does. */
export type CalcType =
  | 'date_diff' // elapsed years/months/days from a date — Age from Date of Birth
  | 'sum' // numeric total
  | 'list_collect'; // gather values into a list (e.g. every household age)

/** Whether the sources are on this record, or across linked records. */
export type CalcScope = 'same_record' | 'cross_record';

/** Which linked people a cross-record roll-up walks. */
export type CalcAcross = 'household_members' | 'household_family_members' | 'household_people';

export type CalcUnit = 'years' | 'months' | 'days';

export interface ComputedFormula {
  fn: CalcType | string;
  scope?: CalcScope;
  /** same-record sources (field names or ids) */
  inputs?: (string | number)[];
  /** cross-record source (a single field name or id) */
  source?: string | number;
  across?: CalcAcross;
  unit?: CalcUnit;
  /** legacy Forms ops carry extra keys (weights, buckets) — preserved as-is */
  [key: string]: unknown;
}

export interface CustomField {
  id: number;
  cbo_id: number;
  name: string;
  type: FieldType;
  friendly_name: string;
  category?: string;
  default_value?: string;
  mandatory: boolean;
  str_len?: number;
  nullable: boolean;
  hidden: boolean;
  field_options?: FieldOption[] | null;
  visibility_rules?: VisibilityRules | null;
  // Which record type(s) this custom field applies to. NULL/undefined/empty =
  // inherit (shown for both members and contacts). See RecordTypeScope.
  applies_to_record_types?: RecordTypeScope[] | null;
  /** Present => the field is calculated and read-only (TRUS-383). */
  computed_formula?: ComputedFormula | null;
  /** "The case is open when these hold" — flags an off-list value for reassignment (TRUS-716). */
  reassign_rules?: ReassignRules | null;
}

export interface CustomFieldCreate {
  name: string;
  type: FieldType;
  friendly_name: string;
  category?: string;
  default_value?: string;
  mandatory?: boolean;
  str_len?: number;
  nullable?: boolean;
  hidden?: boolean;
  field_options?: FieldOption[];
  visibility_rules?: VisibilityRules | null;
  // Record-type scope. Omit / null / empty = applies to both members & contacts.
  applies_to_record_types?: RecordTypeScope[] | null;
  /** The calculation rule. Send null to make the field manually entered again. */
  computed_formula?: ComputedFormula | null;
}

export interface FieldMapping {
  id: number;
  cbo_id: number;
  db_column: string;
  alternative: string;
  source_system?: string;
  description?: string;
  is_active: boolean;
}

export interface FieldMappingCreate {
  db_column: string;
  alternative: string;
  source_system?: string;
  description?: string;
  is_active?: boolean;
}

// ============================================================================
// MEMBER FIELD CONFIGURATION TYPES
// ============================================================================

export type DisplayWidth = 'full' | 'half' | 'third' | 'quarter';

export interface CoreField {
  id: number;
  field_key: string;
  default_display_name: string;
  field_type: string;
  field_group: string;
  is_system_field: boolean;
  sort_order: number;
  // Present for enum core fields (gender, race, ethnicity, preferred_language).
  // Per-cbo overrides live on FieldConfiguration.field_options.
  default_field_options?: FieldOption[] | null;
}

export interface FieldCategory {
  id: number;
  cbo_id: number;
  category_key: string;
  display_name: string;
  description?: string;
  icon?: string;
  sort_order: number;
  is_active: boolean;
  is_collapsed_by_default: boolean;
}

export interface FieldCategoryCreate {
  category_key: string;
  display_name: string;
  description?: string;
  icon?: string;
  sort_order?: number;
  is_collapsed_by_default?: boolean;
}

export interface FieldCategoryUpdate {
  display_name?: string;
  description?: string;
  icon?: string;
  sort_order?: number;
  is_collapsed_by_default?: boolean;
  is_active?: boolean;
}

// Record-type scope vocabulary and helpers live in ./recordTypes so they can be imported
// without instantiating the API client — members.ts constructs a MembersAPI singleton at
// module load, which a unit test for a pure label function should not have to pay for.
export { RECORD_TYPE_SCOPES, recordTypeScopeLabel, normalizeAppliesToScope } from './recordTypes';
export type { RecordTypeScope } from './recordTypes';
import type { RecordTypeScope } from './recordTypes';

export interface FieldConfiguration {
  id: number;
  cbo_id: number;
  core_field_id?: number;
  custom_field_id?: number;
  category_id?: number;
  display_name?: string;
  placeholder_text?: string;
  help_text?: string;
  is_hidden: boolean;
  is_required: boolean;
  is_readonly: boolean;
  sort_order: number;
  // Whether this field is a column in the member LIST view (+ its order).
  show_in_list?: boolean;
  list_sort_order?: number;
  display_width: DisplayWidth;
  validation_regex?: string;
  validation_message?: string;
  // Per-cbo scope override; NULL/undefined = inherit from underlying field
  applies_to_record_types?: RecordTypeScope[] | null;
  // Per-cbo override of the option list for an enum core field (e.g. gender).
  // NULL/undefined = inherit defaults from CoreField.default_field_options.
  field_options?: FieldOption[] | null;
  // Computed fields
  field_key?: string;
  default_display_name?: string;
  effective_display_name?: string;
  category_name?: string;
}

export interface FieldConfigurationCreate {
  core_field_key?: string;
  custom_field_id?: number;
  category_id?: number;
  display_name?: string;
  placeholder_text?: string;
  help_text?: string;
  is_hidden?: boolean;
  is_required?: boolean;
  is_readonly?: boolean;
  sort_order?: number;
  show_in_list?: boolean;
  list_sort_order?: number;
  display_width?: DisplayWidth;
  validation_regex?: string;
  validation_message?: string;
  applies_to_record_types?: RecordTypeScope[] | null;
  field_options?: FieldOption[] | null;
}

export interface FieldConfigurationUpdate {
  // `null` clears the category (returns the field to its default group). It must be
  // representable so the payload survives JSON serialization — `undefined` is dropped
  // by JSON.stringify, leaving an empty body the backend rejects 400 (TRUS-474).
  category_id?: number | null;
  display_name?: string;
  placeholder_text?: string;
  help_text?: string;
  is_hidden?: boolean;
  is_required?: boolean;
  is_readonly?: boolean;
  sort_order?: number;
  show_in_list?: boolean;
  list_sort_order?: number;
  display_width?: DisplayWidth;
  validation_regex?: string;
  validation_message?: string;
  applies_to_record_types?: RecordTypeScope[] | null;
  field_options?: FieldOption[] | null;
}

/** A field configured to appear as a column in the member LIST view. */
export interface MemberListColumn {
  field_key: string;
  display_name: string;
  field_type: string;
  is_core_field: boolean;
  list_sort_order: number;
}

/** A field configured to appear in the Advanced Filters dropdown. */
export interface MemberAdvancedFilter {
  field_key: string;
  display_name: string;
  field_type: string;
  is_core_field: boolean;
  filter_order: number;
  options?: Array<{ value: string; label: string }> | null;
  /**
   * Backend-derived list of values currently present in the org's member
   * data for this field, capped at 50 alphabetical entries. Frontend uses
   * these as dropdown options when the admin hasn't explicitly enumerated
   * options. Null when the field is high-cardinality (>50 distinct values)
   * or lookup failed — frontend then falls back to text-input mode.
   */
  distinct_values?: string[] | null;
}

export interface ProfileField {
  field_key: string;
  field_type: string;
  display_name: string;
  category_key: string;
  category_name: string;
  category_sort: number;
  is_core_field: boolean;
  is_hidden: boolean;
  is_required: boolean;
  is_readonly: boolean;
  field_sort: number;
  display_width: string;
  placeholder_text?: string;
  help_text?: string;
  /** TRUS-401/403: enough to render the input, not just list it.
   *  Options are the org's override falling back to the field's own. */
  field_options?: FieldOption[] | null;
  visibility_rules?: VisibilityRules | null;
}

export interface BulkFieldConfigUpdate {
  field_key: string;
  is_core_field?: boolean;
  display_name?: string;
  category_key?: string;
  is_hidden?: boolean;
  is_required?: boolean;
  sort_order?: number;
}

// A per-program override layered on top of the org base field config.
// is_visible=false hides the field for this program only; is_required and
// sort_order override the base when non-null.
export interface ProgramFieldOverride {
  field_key: string;
  is_core_field: boolean;
  is_visible: boolean;
  is_required?: boolean | null;
  sort_order?: number | null;
}

// Cross-program comparison matrix (field rows × program columns).
export interface ProgramFieldCell {
  program_id: number;
  is_hidden: boolean;
  is_required: boolean;
}

export interface ProgramCompareRow {
  field_key: string;
  is_core_field: boolean;
  display_name: string;
  category_name: string;
  // program_id (as string key) -> cell
  cells: Record<string, ProgramFieldCell>;
}

export interface ProgramCompareResponse {
  programs: Array<{ id: number; name: string }>;
  rows: ProgramCompareRow[];
}

// AI-assisted field configuration (Phase 3).
export interface AIGeneratedField {
  name: string;
  friendly_name: string;
  type: FieldType;
  category?: string;
  mandatory?: boolean;
  help_text?: string;
  options?: FieldOption[];
}

export interface AIFieldDraft {
  categories?: Array<{ display_name: string; description?: string }>;
  fields: AIGeneratedField[];
  notes?: string;
}

export interface AIGenerateResponse {
  draft: AIFieldDraft;
  model: string;
  input_tokens: number;
  output_tokens: number;
  cost_usd: number;
}

export interface AISuggestion {
  kind: string;
  field_key?: string;
  recommendation: string;
  reason: string;
  confidence?: number;
}

export interface AISuggestResponse {
  suggestions: AISuggestion[];
  model: string;
  input_tokens: number;
  output_tokens: number;
  cost_usd: number;
}

export interface MemberBillingLineItem {
  line_number: number;
  cpt_code: string | null;
  hcpcs_code: string | null;
  modifier_1: string | null;
  service_date: string | null;
  units: number;
  charge_amount: number;
  allowed_amount: number;
  paid_amount: number;
  line_status: string | null;
  adjustment_reason_code: string | null;
  service_description: string | null;
  ai_denial_probability: number;
  ai_verdict: string | null;
}

export interface MemberBillingDenial {
  denial_date: string | null;
  denial_reason_code: string | null;
  denial_reason_description: string | null;
  group_code: string | null;
  denied_amount: number;
  resolution_status: string | null;
  resolution_amount: number;
  appeal_number: string | null;
  appeal_status: string | null;
  decision_outcome: string | null;
  approved_amount: number;
  decision_date: string | null;
}

export interface MemberBillingPayment {
  era_control_number: string | null;
  check_eft_number: string | null;
  check_eft_date: string | null;
  payment_method: string | null;
  paid_amount: number;
  charge_amount: number;
  match_confidence: number;
  processing_status: string | null;
}

export interface MemberBillingClaim {
  id: number;
  claim_number: string;
  program_id: number | null;
  payer_id: number | null;
  claim_status: string | null;
  service_date_from: string | null;
  service_date_to: string | null;
  total_charge: number;
  total_allowed: number;
  total_paid: number;
  diagnosis_codes: string[];
  payer_claim_number: string | null;
  payment_date: string | null;
  submission_method: string | null;
  clearinghouse_status: string | null;
  created_ts: string | null;
  payer_name: string | null;
  program_name: string | null;
  line_items: MemberBillingLineItem[];
  denials: MemberBillingDenial[];
  payments: MemberBillingPayment[];
}

export interface MemberBillingSummary {
  member_id: number;
  totals: {
    claim_count: number;
    billed: number;
    paid: number;
    denied_amount: number;
    recovered_amount: number;
    paid_claims: number;
    denied_claims: number;
  };
  claims: MemberBillingClaim[];
}

export interface UnifiedViewSettings {
  id: number;
  cbo_id: number;
  show_all_enrollments: boolean;
  show_enrollment_history: boolean;
  group_by_program: boolean;
  use_global_member_data: boolean;
  highlight_data_conflicts: boolean;
  show_family_members: boolean;
  show_caregivers: boolean;
  show_identifiers: boolean;
  show_activities: boolean;
  show_milestones: boolean;
  show_documents: boolean;
  show_communications: boolean;
  // Additional section visibility
  show_households: boolean;
  show_referrals: boolean;
  show_case_episodes: boolean;
  show_consent: boolean;
  show_signatures: boolean;
  show_forms: boolean;
  show_tasks: boolean;
  show_notifications: boolean;
  show_data_conflicts: boolean;
  show_case_history: boolean;
  show_ai_enrichment: boolean;
  show_mco_delegation: boolean;
  show_workflows: boolean;
  show_engagement: boolean;
  show_incentives: boolean;
  show_engagement_intel: boolean;
  show_mail: boolean;
  show_assessments: boolean;
  show_coordination: boolean;
  show_health_connect: boolean;
  show_billing: boolean;
  /**
   * Referrals the org makes OUTSIDE the platform (TRUS-511). Separate from
   * show_referrals/show_coordination, which gate the clinical provider-matching tab —
   * TRUS-519 turns that off for the same org that asked for this, so the two must be
   * independently switchable. Opt-in: absent reads as off.
   */
  show_external_referrals?: boolean;
}

/**
 * One element of the member-profile nav tree (TRUS-528).
 *
 * `label` is already resolved server-side: the org's own wording if it set
 * one (translated, if the org has Spanish for it), else the platform default.
 * Render `label` ONLY when `label_override` is set — otherwise go through
 * `t(i18n_key, default_label)`, because the clients' own bundles already carry
 * a complete translation of every platform default and the server overlay does
 * not duplicate it.
 */
export interface ProfileLayoutElement {
  element_key: string;
  label: string;
  default_label: string;
  label_override: string | null;
  i18n_key: string;
  display_order: number;
  default_order: number;
  is_customized: boolean;
}

export interface ProfileLayoutTab extends ProfileLayoutElement {
  tab_key: string;
  section_key: string;
  parent_tab_key: string | null;
  settings_columns: string[];
  /** Visibility follows the member's data/capabilities, not an org switch. */
  data_gated: boolean;
  /** The anchor of its area; the org cannot switch it off. */
  always_visible: boolean;
  /** Effective visibility from member_unified_view_settings + program overrides. */
  visible: boolean;
  sub_tabs?: ProfileLayoutTab[];
  /** Present only for an org's own note-category sections (TRUS-420). */
  note_category_id?: number;
  rename_via?: 'note_categories' | 'pinned_forms';
  /**
   * Pinned-form tabs (TRUS-564 / TRUS-762): a form template on the profile for
   * one program, keyed `pinned-form-{id}`. On a member's layout the server has
   * already dropped pins for programs the member is not in and for roles the
   * user does not hold, and resolved the enrollment the form attaches to.
   */
  pinned_form_id?: number;
  form_template_id?: number;
  program_id?: number;
  program_name?: string;
  /** null = every role. */
  roles?: string[] | null;
  enrollment_id?: number | null;
  /**
   * TRUS-762. Where a top-level tab is listed: the left rail or the Member
   * setup sheet. Absent from a server that predates it; the client then
   * uses its own defaults (see MemberProfileNew's FALLBACK_PLACEMENT).
   */
  placement?: ProfilePlacement;
  default_placement?: ProfilePlacement;
  placement_overridable?: boolean;
  /** A retired tab whose content now lives in this tab (never listed itself). */
  alias_of?: string | null;
  /** Rendered as a segment of this hub tab instead of an entry of its own. */
  segment_of?: string | null;
  /** The segment / panel of the alias target an old key opens. */
  alias_segment?: string | null;
}

export type ProfilePlacement = 'rail' | 'setup';

export interface ProfileLayoutSection extends ProfileLayoutElement {
  section_key: string;
  tabs: ProfileLayoutTab[];
}

export interface ProfileLayout {
  sections: ProfileLayoutSection[];
  /** show_* fields that are deliberately not nav elements, with the reason. */
  non_tab_settings: Record<string, string>;
  /** TRUS-762: old top-level tab keys and where they open now. */
  aliases?: Record<string, { tab_key: string; segment: string | null }>;
}

export interface ProfileLayoutOverrideInput {
  scope: 'section' | 'tab';
  element_key: string;
  /** null (or "") clears the rename. Both fields null deletes the row. */
  label_override?: string | null;
  display_order?: number | null;
  /** TRUS-762. Omit to keep the org's placement; null resets it to the default. */
  placement?: ProfilePlacement | null;
}

/** A form template pinned to the member profile for one program (TRUS-564), shown to some roles (TRUS-762). */
export interface PinnedForm {
  id: number;
  program_id: number;
  form_template_id: number;
  /** The admin's label, else the template's name. */
  label: string;
  display_order: number;
  program_name: string;
  /** Role keys the pin is shown to; null = every role. */
  roles: string[] | null;
  /** False when the pin's program does not offer its form (it opens nothing, so members never see it). */
  available?: boolean;
}

export interface PinnedFormCreateInput {
  program_id: number;
  form_template_id: number;
  label_override?: string | null;
  display_order?: number;
  /** null or [] = every role. */
  roles?: string[] | null;
}

export interface PinnedFormUpdateInput {
  label_override?: string | null;
  display_order?: number;
  is_active?: boolean;
  /** Omit to keep; null or [] = every role. */
  roles?: string[] | null;
  program_id?: number;
}

export interface UnifiedViewSettingsUpdate {
  show_all_enrollments?: boolean;
  show_enrollment_history?: boolean;
  group_by_program?: boolean;
  use_global_member_data?: boolean;
  highlight_data_conflicts?: boolean;
  show_family_members?: boolean;
  show_caregivers?: boolean;
  show_identifiers?: boolean;
  show_activities?: boolean;
  show_milestones?: boolean;
  show_documents?: boolean;
  show_communications?: boolean;
  show_households?: boolean;
  show_referrals?: boolean;
  show_case_episodes?: boolean;
  show_consent?: boolean;
  show_signatures?: boolean;
  show_forms?: boolean;
  show_tasks?: boolean;
  show_notifications?: boolean;
  show_data_conflicts?: boolean;
  show_case_history?: boolean;
  show_ai_enrichment?: boolean;
  show_mco_delegation?: boolean;
  show_workflows?: boolean;
  show_engagement?: boolean;
  show_incentives?: boolean;
  show_engagement_intel?: boolean;
  show_mail?: boolean;
  show_assessments?: boolean;
  show_coordination?: boolean;
  show_health_connect?: boolean;
  show_billing?: boolean;
  show_external_referrals?: boolean;
}

export interface ConfiguredField {
  field_key: string;
  field_type: string;
  display_name: string;
  value: any;
  category_key: string;
  category_name: string;
  is_core_field: boolean;
  is_hidden: boolean;
  is_required: boolean;
  is_readonly: boolean;
  sort_order: number;
  display_width: string;
  placeholder_text?: string;
  help_text?: string;
}

export interface ConfiguredCategory {
  category_key: string;
  category_name: string;
  sort_order: number;
  fields: ConfiguredField[];
}

export interface MemberProfileConfigured {
  member: Member;
  configured_fields: ConfiguredField[];
  categories: ConfiguredCategory[];
  enrollments: Enrollment[];
  unified_settings: Partial<UnifiedViewSettings>;
}

// ============================================================================
// API CLIENT
// ============================================================================

class MembersAPI {
  private client: AxiosInstance;

  constructor(baseURL: string = (productConfig.weaveinApi.baseUrl ?? '').trim()) {
    // Initialize active CBO ID from JWT token if not already set
    initializeActiveCboId();

    // Use empty baseURL in production to leverage Vercel's proxy rules
    // In development, use environment variable if set
    this.client = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
        'X-API-Version': API_VERSION,
      },
    });

    // Add auth token interceptor
    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      } else {
        console.warn('📋 Members/CustomFields API: No auth token found in localStorage');
      }
      if (cboId) {
        config.headers['X-CBO-ID'] = cboId;
      }

      // TRUS-446 — the member profile's field labels, category headings and
      // picklist option labels are resolved server-side by
      // /api/member-field-config/profile-fields, and this header is what
      // selects the language. Without it the API falls back to the saved user
      // preference and serves English.
      applyUiLanguageHeader(config);

      console.log('📋 Members/CustomFields API Request:', {
        method: config.method?.toUpperCase(),
        url: config.url,
        hasToken: !!token,
        cboId: cboId || 'none'
      });
      return config;
    });

    // Add response error interceptor for detailed logging
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error.response?.status;
        const url = error.config?.url;
        const method = error.config?.method?.toUpperCase();

        if (status === 401) {
          console.error('📋 Members/CustomFields API 401 Unauthorized:', {
            url,
            method,
            message: error.response?.data?.detail || error.message,
            hasToken: !!localStorage.getItem('auth_token'),
            cboId: localStorage.getItem('active_cbo_id') || 'none'
          });
        } else if (status === 405) {
          console.error('📋 Members/CustomFields API 405 Method Not Allowed:', {
            url,
            method,
            message: error.response?.data?.detail || error.message,
            hint: 'This endpoint may not support the requested HTTP method - check backend route configuration'
          });
        } else {
          console.error('📋 Members/CustomFields API Error:', {
            status,
            url,
            method,
            message: error.response?.data?.detail || error.message,
            requestBody: error.config?.data,
          });
        }
        return Promise.reject(error);
      }
    );
  }

  // ========================================================================
  // MEMBER ENDPOINTS
  // ========================================================================

  async createMember(data: MemberCreate): Promise<Member> {
    const { offlineAwareMutate } = await import('@/lib/offline/hooks');
    const res: any = await offlineAwareMutate('CREATE', 'member-profile', undefined, data, async () => {
      const response = await this.client.post<Member>('/api/members/', data);
      return response.data;
    });
    return res && res.queued ? ({ id: -Date.now(), ...data } as unknown as Member) : res;
  }

  async listMembers(params?: {
    page?: number;
    page_size?: number;
    search?: string;
    program_id?: number;
    enrollment_status?: string;
    group_id?: number;
    coordinator_id?: number;
    gender?: string;
    language?: string;
    insurance_type?: string;
    custom_field?: string;
    filter_chip?: string;
    /**
     * Which membership links to return: 'active' (default), 'archived' for
     * soft-deleted members only, or 'all'. This is how the UI finds a member
     * somebody archived — it needs no per-organization chip configuration,
     * unlike `filter_chip` (TKT-20260902-59BC4E).
     */
    membership_status?: 'active' | 'archived' | 'all';
    workforce_only?: boolean;
    sort_by?: string;
    sort_order?: string;
  }): Promise<MemberListResponse> {
    const cboId = localStorage.getItem('active_cbo_id');
    const token = localStorage.getItem('auth_token');

    // Use offline-aware API client for GET requests
    try {
      const response = await offlineGet<MemberListResponse>('/api/members/', {
        params,
        headers: {
          'Content-Type': 'application/json',
          'X-API-Version': API_VERSION,
          ...(token && { Authorization: `Bearer ${token}` }),
          ...(cboId && { 'X-CBO-ID': cboId }),
        },
        cacheOptions: {
          ttl: 30 * 60 * 1000, // 30 minutes cache
        },
      });
      return response.data;
    } catch (error: any) {
      // If offline with no cache, return empty result instead of throwing
      if (!isCurrentlyOnline() && error.message?.includes('no cached data')) {
        console.warn('[Members] Offline with no cached data, returning empty list');
        return { members: [], total: 0, page: params?.page || 1, page_size: params?.page_size || 50 };
      }
      throw error;
    }
  }

  async getMember(memberId: number): Promise<Member> {
    const response = await this.client.get<Member>(`/api/members/${memberId}`);
    return response.data;
  }

  async updateMember(memberId: number, data: Partial<MemberBase>): Promise<Member> {
    const response = await this.client.put<Member>(`/api/members/${memberId}`, data);
    return response.data;
  }

  /**
   * Archives the member in this organization — a SOFT delete. The member row and every
   * custom-field value survive; only `member_organizations.status` becomes 'inactive'.
   * Reversible via `restoreMember`.
   */
  async deleteMember(memberId: number): Promise<void> {
    await this.client.delete(`/api/members/${memberId}`);
  }

  /**
   * Undo an archive. Same permission as deleting — whoever may archive a member may bring
   * one back, so staff don't have to raise a support ticket to undo their own click
   * (TRUS-371).
   */
  async restoreMember(memberId: number): Promise<{ id: number; status: string; previous_status: string }> {
    const response = await this.client.post(`/api/members/${memberId}/restore`);
    return response.data;
  }

  // ==========================================================================
  // Duplicate member review (TRUS-425 / TRUS-426)
  // ==========================================================================

  /**
   * Duplicate candidates for the active organization, strongest signal first.
   * Groups are computed server-side on every call, so they always reflect the
   * current records; only merged/dismissed decisions are stored.
   */
  async listDuplicateMembers(params?: {
    min_confidence?: DuplicateConfidence;
    include_resolved?: boolean;
    limit?: number;
  }): Promise<DuplicateGroupsResponse> {
    const response = await this.client.get<DuplicateGroupsResponse>(
      '/api/members/duplicates',
      { params }
    );
    return response.data;
  }

  /**
   * Whether the current user may resolve duplicates (merge, keep both, undo) in
   * the active organization: admin roles, or a role granted
   * `members.merge_duplicates` (TRUS-788).
   */
  async getDuplicatePermissions(): Promise<{ can_resolve: boolean }> {
    const response = await this.client.get<{ can_resolve: boolean }>(
      '/api/members/duplicates/permissions'
    );
    return response.data;
  }

  /**
   * The same duplicate candidates as a CSV, one row per member record with every
   * field on it — what an org resolving a whole migration backlog works from,
   * rather than comparing pairs on screen one at a time (TRUS-518).
   */
  async exportDuplicateMembers(params?: {
    min_confidence?: DuplicateConfidence;
    include_resolved?: boolean;
    limit?: number;
  }): Promise<Blob> {
    const response = await this.client.get('/api/members/duplicates/export', {
      params,
      responseType: 'blob',
    });
    return response.data as Blob;
  }

  /**
   * Merge duplicate profiles into the survivor.
   *
   * Non-destructive: content moves onto the survivor and each duplicate is
   * archived, never deleted. Pass `dry_run` to get the same per-table counts
   * back without committing, so the UI can show what a merge would move.
   */
  async mergeDuplicateMembers(data: {
    survivor_id: number;
    duplicate_ids: number[];
    match_reason?: string;
    notes?: string;
    dry_run?: boolean;
    /** Per-field winner: {field: member id whose value wins}. Fields are back-fill
     *  member columns ('email') or org fields ('field.<field_id>'). */
    field_choices?: Record<string, number>;
    /** {duplicate enrollment id: 'combine' | 'dont_merge'} — required for every
     *  duplicate case in a program the survivor already has a case in. Any
     *  'dont_merge' is refused (400 merge_declined_cases): dismiss the group instead. */
    case_decisions?: Record<string, CaseDecision>;
  }): Promise<DuplicateMergeResult> {
    const response = await this.client.post<DuplicateMergeResult>(
      '/api/members/duplicates/merge',
      data
    );
    return response.data;
  }

  /**
   * What the merged profile would look like, field by field (TRUS-558): each
   * record's value, the value the merge leaves, and where they conflict. Read-only;
   * `moved` carries the same per-table counts as a dry-run merge.
   * `duplicateIds` order matters — the first duplicate with a value fills a blank.
   */
  async previewDuplicateMerge(
    survivorId: number,
    duplicateIds: number[]
  ): Promise<DuplicateMergePreview> {
    const response = await this.client.get<DuplicateMergePreview>(
      '/api/members/duplicates/preview',
      { params: { survivor_id: survivorId, duplicate_ids: duplicateIds.join(',') } }
    );
    return response.data;
  }

  /** Past merges for the active organization, newest first (TRUS-558). */
  async listDuplicateMerges(limit = 100): Promise<MergeHistoryResponse> {
    const response = await this.client.get<MergeHistoryResponse>('/api/members/duplicates/merges', {
      params: { limit },
    });
    return response.data;
  }

  /**
   * Undo a merge, once. Values edited since the merge are kept and listed in
   * `revert_report.conflicts`; records created after the merge stay on the survivor.
   */
  async revertDuplicateMerge(journalId: number): Promise<MergeRevertResult> {
    const response = await this.client.post<MergeRevertResult>(
      `/api/members/duplicates/merges/${journalId}/revert`
    );
    return response.data;
  }

  /** "Different people" dismissals for the active organization, newest first. */
  async listDuplicateDismissals(limit = 100): Promise<{ cbo_id: number; total: number; dismissals: DuplicateDismissal[] }> {
    const response = await this.client.get('/api/members/duplicates/dismissals', { params: { limit } });
    return response.data;
  }

  /** Undo a dismissal so the group shows up in duplicate review again. */
  async undoDuplicateDismissal(
    resolutionId: number
  ): Promise<{ success: boolean; resolution_id: number; member_ids: number[] }> {
    const response = await this.client.post(`/api/members/duplicates/dismissals/${resolutionId}/undo`);
    return response.data;
  }

  /** Record that these profiles are genuinely different people. */
  async dismissDuplicateMembers(data: {
    member_ids: number[];
    match_reason?: string;
    notes?: string;
  }): Promise<{ success: boolean; dismissed: number[] }> {
    const response = await this.client.post('/api/members/duplicates/dismiss', data);
    return response.data;
  }

  async lookupMemberByMedicaidId(medicaidId: string): Promise<MemberLookupResponse> {
    const response = await this.client.get<MemberLookupResponse>(
      `/api/members/lookup/medicaid/${medicaidId}`
    );
    return response.data;
  }

  async getMemberReferrals(memberId: number): Promise<Referral[]> {
    const response = await this.client.get<Referral[]>(`/api/members/${memberId}/referrals`);
    return response.data;
  }

  async getMemberBillingSummary(memberId: number): Promise<MemberBillingSummary> {
    const response = await this.client.get<MemberBillingSummary>(
      `/api/claims/member/${memberId}/billing-summary`
    );
    return response.data;
  }

  async getMemberEnrollments(memberId: number): Promise<Enrollment[]> {
    const response = await this.client.get<Enrollment[]>(`/api/members/${memberId}/enrollments`);
    return response.data;
  }

  async getMemberConflicts(memberId: number): Promise<DataConflict[]> {
    const response = await this.client.get<DataConflict[]>(`/api/members/${memberId}/conflicts`);
    return response.data;
  }

  // ========================================================================
  // CUSTOM FIELDS ENDPOINTS
  // ========================================================================

  async createCustomField(data: CustomFieldCreate): Promise<CustomField> {
    const response = await this.client.post<CustomField>('/api/custom-fields', data);
    return response.data;
  }

  async listCustomFields(params?: {
    category?: string;
    include_hidden?: boolean;
    record_type?: 'member' | 'contact' | 'activity_event' | 'family_member' | 'household' | 'caregiver';
    /** Include fields belonging to a repeatable field group. Defaults to false
     *  server-side so entry forms never render a group child as a standalone
     *  field (TRUS-302). Only the field-group / field-config admin screens,
     *  which exist to configure those children, should pass true. */
    include_group_children?: boolean;
  }): Promise<CustomField[]> {
    // Note: Backend requires no trailing slash for GET requests
    const response = await this.client.get<CustomField[]>('/api/custom-fields', { params });
    return response.data;
  }

  async getCustomField(fieldId: number): Promise<CustomField> {
    const response = await this.client.get<CustomField>(`/api/custom-fields/${fieldId}`);
    return response.data;
  }

  async updateCustomField(
    fieldId: number,
    // Name is immutable. Type can now be changed (TRUS-59 #1) — if the new
    // type needs options, the backend requires `field_options` in the same
    // payload (or already on the row). Use `getCustomFieldDistinctValues`
    // to pre-populate them from existing data.
    data: Partial<Omit<CustomFieldCreate, 'name'>>
  ): Promise<CustomField> {
    const response = await this.client.put<CustomField>(`/api/custom-fields/${fieldId}`, data);
    return response.data;
  }

  /**
   * Fetch the distinct non-empty values currently stored for a custom field.
   * Returned as `{value, label}` pairs so they can be dropped straight into a
   * select-type field's options after the admin reviews them.
   */
  async getCustomFieldDistinctValues(fieldId: number): Promise<FieldOption[]> {
    const response = await this.client.get<FieldOption[]>(
      `/api/custom-fields/${fieldId}/distinct-values`
    );
    return response.data;
  }

  async deleteCustomField(fieldId: number): Promise<void> {
    await this.client.delete(`/api/custom-fields/${fieldId}`);
  }

  async listFieldCategories(): Promise<string[]> {
    const response = await this.client.get<string[]>('/api/custom-fields/categories');
    return response.data;
  }

  async bulkCreateFields(fields: CustomFieldCreate[]): Promise<CustomField[]> {
    const response = await this.client.post<CustomField[]>('/api/custom-fields/bulk-create', fields);
    return response.data;
  }

  // ========================================================================
  // FIELD MAPPING ENDPOINTS
  // ========================================================================

  async createFieldMapping(data: FieldMappingCreate): Promise<FieldMapping> {
    const response = await this.client.post<FieldMapping>('/api/custom-fields/mappings', data);
    return response.data;
  }

  async listFieldMappings(dbColumn?: string): Promise<FieldMapping[]> {
    const response = await this.client.get<FieldMapping[]>('/api/custom-fields/mappings', {
      params: dbColumn ? { db_column: dbColumn } : undefined,
    });
    return response.data;
  }

  async deleteFieldMapping(mappingId: number): Promise<void> {
    await this.client.delete(`/api/custom-fields/mappings/${mappingId}`);
  }

  /**
   * Bulk-create field mappings. Backend returns {created, skipped[reason]} —
   * one bad row doesn't fail the batch. Used by FieldMappingManager's CSV import.
   */
  async bulkCreateFieldMappings(
    mappings: FieldMappingCreate[]
  ): Promise<{ created: FieldMapping[]; skipped: Array<{ db_column: string; alternative: string; reason: string }> }> {
    const response = await this.client.post('/api/custom-fields/mappings/bulk', mappings);
    return response.data;
  }

  /**
   * Mappable core field keys, sourced server-side from member_core_fields
   * registry + non-registry families (family member, household, caregiver).
   * Use this instead of hardcoding a list in the UI.
   */
  async listMappableCoreFieldKeys(): Promise<string[]> {
    const response = await this.client.get<string[]>('/api/custom-fields/core-fields');
    return response.data;
  }

  // ========================================================================
  // MEMBER FIELD CONFIGURATION ENDPOINTS
  // ========================================================================

  async getMemberProfileConfigured(
    memberId: number,
    params?: { program_id?: number; include_hidden?: boolean }
  ): Promise<MemberProfileConfigured> {
    const response = await this.client.get<MemberProfileConfigured>(
      `/api/members/${memberId}/profile-configured`,
      { params }
    );
    return response.data;
  }

  /**
   * Effective allowed-value list for a core enum field (gender, race,
   * ethnicity, preferred_language). Returns the per-org override if one exists,
   * else the seeded system defaults. Used by the Profile Builder's "Edit
   * allowed values" editor to pre-seed the option list. (TRUS-100 part 4.)
   */
  async getFieldEffectiveOptions(fieldKey: string): Promise<FieldOption[]> {
    // The endpoint ignores field_key and returns a DICT keyed by field
    // (race/ethnicity/gender/language/country/state), so extract the requested
    // field's array rather than returning the whole object. Returning the raw
    // dict made the allowed-values editor crash on `.map` (TRUS-610).
    // preferred_language maps to the backend "language" key (members.py
    // key_alias at /field-options).
    const response = await this.client.get<Record<string, FieldOption[]>>(
      '/api/members/field-options',
      { params: { field_key: fieldKey } },
    );
    const data = response.data ?? {};
    const keyAlias: Record<string, string> = { preferred_language: 'language' };
    const aliasedKey = keyAlias[fieldKey] ?? fieldKey;
    const opts = data[aliasedKey] ?? data[fieldKey];
    return Array.isArray(opts) ? opts : [];
  }

  async listCoreFields(params?: { record_type?: 'member' | 'contact' | 'activity_event' }): Promise<CoreField[]> {
    const response = await this.client.get<CoreField[]>('/api/member-field-config/core-fields', {
      params,
    });
    return response.data;
  }

  // Member Field Categories (for profile configuration)
  async listMemberFieldCategories(includeInactive?: boolean): Promise<FieldCategory[]> {
    const response = await this.client.get<FieldCategory[]>('/api/member-field-config/categories', {
      params: { include_inactive: includeInactive },
    });
    return response.data;
  }

  async createFieldCategory(data: FieldCategoryCreate): Promise<FieldCategory> {
    const response = await this.client.post<FieldCategory>('/api/member-field-config/categories', data);
    return response.data;
  }

  async updateFieldCategory(categoryId: number, data: FieldCategoryUpdate): Promise<FieldCategory> {
    const response = await this.client.put<FieldCategory>(
      `/api/member-field-config/categories/${categoryId}`,
      data
    );
    return response.data;
  }

  async deleteFieldCategory(categoryId: number): Promise<void> {
    await this.client.delete(`/api/member-field-config/categories/${categoryId}`);
  }

  // Field Configurations
  async listFieldConfigurations(params?: {
    include_hidden?: boolean;
    category_id?: number;
    record_type?: 'member' | 'contact' | 'activity_event' | 'family_member';
  }): Promise<FieldConfiguration[]> {
    const response = await this.client.get<FieldConfiguration[]>(
      '/api/member-field-config/configurations',
      { params }
    );
    return response.data;
  }

  async createFieldConfiguration(data: FieldConfigurationCreate): Promise<FieldConfiguration> {
    const response = await this.client.post<FieldConfiguration>(
      '/api/member-field-config/configurations',
      data
    );
    return response.data;
  }

  async updateFieldConfiguration(
    configId: number,
    data: FieldConfigurationUpdate
  ): Promise<FieldConfiguration> {
    const response = await this.client.put<FieldConfiguration>(
      `/api/member-field-config/configurations/${configId}`,
      data
    );
    return response.data;
  }

  async deleteFieldConfiguration(configId: number): Promise<void> {
    await this.client.delete(`/api/member-field-config/configurations/${configId}`);
  }

  async bulkUpdateFieldConfigurations(
    updates: BulkFieldConfigUpdate[]
  ): Promise<{ updated: number; created: number; errors: string[] }> {
    const response = await this.client.post<{ updated: number; created: number; errors: string[] }>(
      '/api/member-field-config/configurations/bulk',
      updates
    );
    return response.data;
  }

  // TRUS-109 — Field-map round-trip. Download every data field with the 15 setup
  // columns (name/display/type/values + per-context MIF/Assessment/Care Plan/Billing/
  // RTF flags, names, tooltips, logic) as .xlsx, edit, and re-upload to apply.
  async exportFieldMap(): Promise<Blob> {
    const response = await this.client.get('/api/member-field-config/field-map/export', {
      responseType: 'blob',
    });
    return response.data as Blob;
  }

  async importFieldMap(file: File): Promise<{ fields_updated: number; rows_skipped: number; skipped_examples: string[] }> {
    const form = new FormData();
    form.append('file', file);
    const response = await this.client.post('/api/member-field-config/field-map/import', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  // Profile Fields (combined view)
  async getProfileFields(params?: {
    program_id?: number;
    include_hidden?: boolean;
    // Every scope the unified field function supports. 'household' and
    // 'caregiver' were missing here, which is part of why those record surfaces
    // fell back to the flat custom-fields endpoint (TRUS-401/403).
    record_type?: RecordTypeScope;
  }): Promise<ProfileField[]> {
    const response = await this.client.get<ProfileField[]>(
      '/api/member-field-config/profile-fields',
      { params }
    );
    return response.data;
  }

  // Per-program field overrides. The org owns one base field library; each
  // program may layer visible/required/order overrides on top. Returns only
  // the fields that have an explicit override — overlay these on the base
  // list from getProfileFields().
  async listProgramFieldOverrides(programId: number): Promise<ProgramFieldOverride[]> {
    const response = await this.client.get<ProgramFieldOverride[]>(
      `/api/member-field-config/programs/${programId}/field-overrides`
    );
    return response.data;
  }

  async setProgramFieldOverrides(
    programId: number,
    overrides: ProgramFieldOverride[]
  ): Promise<{ applied: number; errors: string[] }> {
    const response = await this.client.put<{ applied: number; errors: string[] }>(
      `/api/member-field-config/programs/${programId}/field-overrides`,
      overrides
    );
    return response.data;
  }

  async deleteProgramFieldOverride(
    programId: number,
    fieldKey: string,
    isCoreField: boolean
  ): Promise<void> {
    await this.client.delete(
      `/api/member-field-config/programs/${programId}/field-overrides/${encodeURIComponent(fieldKey)}`,
      { params: { is_core_field: isCoreField } }
    );
  }

  // Cross-program comparison matrix: every active program's effective
  // hidden/required state for each configured field. Replaces the manual
  // spreadsheet diff across programs.
  async compareProgramFields(
    record_type: 'member' | 'contact' = 'member'
  ): Promise<ProgramCompareResponse> {
    const response = await this.client.get<ProgramCompareResponse>(
      '/api/member-field-config/programs/compare',
      { params: { record_type } }
    );
    return response.data;
  }

  // Copy one program's per-program overrides onto another (within the org).
  async cloneProgramOverrides(
    targetProgramId: number,
    sourceProgramId: number,
    mode: 'replace' | 'merge' = 'replace'
  ): Promise<{ cloned: number }> {
    const response = await this.client.post<{ cloned: number }>(
      `/api/member-field-config/programs/${targetProgramId}/clone-overrides`,
      { source_program_id: sourceProgramId, mode }
    );
    return response.data;
  }

  // AI: draft a field config from a program description and/or spreadsheet
  // columns. Returns a draft for human review — nothing is persisted.
  async aiGenerateFieldConfig(body: {
    description?: string;
    columns?: Array<{ name: string; samples?: string[] }>;
  }): Promise<AIGenerateResponse> {
    const response = await this.client.post<AIGenerateResponse>(
      '/api/member-field-config/ai/generate',
      body
    );
    return response.data;
  }

  // AI: suggest improvements to the org's current field configuration.
  async aiSuggestFieldImprovements(
    record_type: 'member' | 'contact' = 'member'
  ): Promise<AISuggestResponse> {
    const response = await this.client.post<AISuggestResponse>(
      '/api/member-field-config/ai/suggest',
      null,
      { params: { record_type } }
    );
    return response.data;
  }

  // Member list columns — fields the org flagged "Show in list" (ordered).
  // Pass program_id to drop columns hidden for that program.
  async getMemberListColumns(
    record_type: 'member' | 'contact' = 'member',
    program_id?: number
  ): Promise<MemberListColumn[]> {
    const response = await this.client.get<MemberListColumn[]>(
      '/api/member-field-config/list-columns',
      { params: { record_type, program_id } }
    );
    return response.data;
  }

  // Advanced filters configured via field config — same field set, different
  // semantic flag (show_in_advanced_filter). One input per row rendered in
  // the Advanced Filters dropdown. Pass program_id to drop filters hidden
  // for that program.
  async getMemberAdvancedFilters(
    record_type: 'member' | 'contact' = 'member',
    program_id?: number
  ): Promise<MemberAdvancedFilter[]> {
    const response = await this.client.get<MemberAdvancedFilter[]>(
      '/api/member-field-config/advanced-filters',
      { params: { record_type, program_id } }
    );
    return response.data;
  }

  // Unified View Settings
  async getUnifiedViewSettings(): Promise<UnifiedViewSettings> {
    const response = await this.client.get<UnifiedViewSettings>(
      '/api/member-field-config/unified-view-settings'
    );
    return response.data;
  }

  async updateUnifiedViewSettings(data: UnifiedViewSettingsUpdate): Promise<UnifiedViewSettings> {
    const response = await this.client.put<UnifiedViewSettings>(
      '/api/member-field-config/unified-view-settings',
      data
    );
    return response.data;
  }

  // Effective view settings for a member = org settings merged with the
  // member's active-program tab overrides (most-permissive, resolved server-side).
  async getEffectiveViewSettings(memberId: number): Promise<UnifiedViewSettings> {
    const response = await this.client.get<UnifiedViewSettings>(
      '/api/member-field-config/effective-view-settings',
      { params: { member_id: memberId } }
    );
    return response.data;
  }

  // Member-profile nav tree: sections, tabs and sub-tabs with this org's
  // terminology and ordering, plus effective visibility (TRUS-528).
  // Pass memberId to resolve visibility against that member's active-program
  // overrides, the same merge /effective-view-settings does.
  async getProfileLayout(memberId?: number): Promise<ProfileLayout> {
    const response = await this.client.get<ProfileLayout>(
      '/api/member-field-config/profile-layout',
      { params: memberId ? { member_id: memberId } : undefined }
    );
    return response.data;
  }

  // Bulk because a drag-to-reorder renumbers a whole section at once; one
  // request per element would leave the rail half-reordered on a failure.
  async updateProfileLayout(
    overrides: ProfileLayoutOverrideInput[]
  ): Promise<{ applied: number; cleared: number }> {
    const response = await this.client.put<{ applied: number; cleared: number }>(
      '/api/member-field-config/profile-layout',
      { overrides }
    );
    return response.data;
  }

  async resetProfileLayout(): Promise<{ cleared: number }> {
    const response = await this.client.delete<{ cleared: number }>(
      '/api/member-field-config/profile-layout'
    );
    return response.data;
  }

  // Pinned forms (TRUS-564, roles TRUS-762). Reads are open to the org — the
  // profile needs them; writes need layout-admin, enforced server-side.
  async getPinnedForms(): Promise<PinnedForm[]> {
    const response = await this.client.get<{ pinned_forms: PinnedForm[] }>(
      '/api/member-field-config/pinned-forms'
    );
    return response.data.pinned_forms ?? [];
  }

  // Re-pinning a removed form reactivates the same row server-side.
  async createPinnedForm(input: PinnedFormCreateInput): Promise<{ id: number }> {
    const response = await this.client.post('/api/member-field-config/pinned-forms', input);
    return response.data;
  }

  async updatePinnedForm(pinnedId: number, input: PinnedFormUpdateInput): Promise<{ id: number; updated: number }> {
    const response = await this.client.put(`/api/member-field-config/pinned-forms/${pinnedId}`, input);
    return response.data;
  }

  async deletePinnedForm(pinnedId: number): Promise<{ id: number; deleted: boolean }> {
    const response = await this.client.delete(`/api/member-field-config/pinned-forms/${pinnedId}`);
    return response.data;
  }

  // Program-scoped tab-visibility overrides. `overrides` only contains tabs the
  // program explicitly overrides; absent keys inherit the org default.
  async getProgramTabVisibility(
    programId: number
  ): Promise<{ program_id: number; overrides: Record<string, boolean> }> {
    const response = await this.client.get<{
      program_id: number;
      overrides: Record<string, boolean>;
    }>('/api/member-field-config/program-tab-visibility', {
      params: { program_id: programId },
    });
    return response.data;
  }

  // Set/clear a single program tab override. isVisible = null clears the
  // override (revert to inherit); true = show; false = hide.
  async updateProgramTabVisibility(
    programId: number,
    tabKey: string,
    isVisible: boolean | null
  ): Promise<any> {
    const response = await this.client.put(
      '/api/member-field-config/program-tab-visibility',
      { program_id: programId, tab_key: tabKey, is_visible: isVisible }
    );
    return response.data;
  }
}

// Export singleton instance - uses env var or empty string for proxy routing
// ============================================================================
// DUPLICATE MEMBER REVIEW (TRUS-425 / TRUS-426)
// ============================================================================

export type DuplicateConfidence = 'low' | 'medium' | 'high';

export interface DuplicateCandidateMember {
  id: number;
  first_name: string | null;
  last_name: string | null;
  dob: string | null;
  phone: string | null;
  email: string | null;
  medicaid_id: string | null;
  member_number: string | null;
  created_at: string | null;
  source_system: string | null;
  source_system_id: string | null;
  legacy_member_id: string | null;
  /** True when the profile was soft-deleted in the source system it came from. */
  legacy_is_deleted: boolean;
  legacy_created_ts: string | null;
  org_status: string | null;
  org_status_reason: string | null;
  documents: number;
  notes: number;
  communications: number;
  field_values: number;
  forms: number;
  /** documents + notes + communications + forms — how much is at stake. */
  content_total: number;
}

export interface DuplicateGroup {
  match_key: string;
  reason: string;
  label: string;
  confidence: DuplicateConfidence;
  suggested_survivor_id: number | null;
  members: DuplicateCandidateMember[];
}

export interface DuplicateGroupsResponse {
  cbo_id: number;
  total_groups: number;
  total_duplicate_profiles: number;
  groups: DuplicateGroup[];
}

/** What the merge does with one duplicate case (program enrollment). */
export type CaseDecision = 'combine' | 'dont_merge';

export interface MergeCase {
  enrollment_id: number;
  member_id: number;
  program_id: number;
  program_name: string | null;
  status: string | null;
  enrollment_date: string | null;
  notes: number;
  docs: number;
  activities: number;
  forms: number;
  draft_forms: number;
  /** The survivor's case in the same program, when there is one. */
  survivor_enrollment_id: number | null;
  survivor_enrollment_status: string | null;
  /** True when the survivor already has a case in this program. */
  decision_required: boolean;
  decision: CaseDecision | null;
  /** move: moves whole; combine; dont_merge (refuses the merge); null: undecided. */
  action: 'move' | 'combine' | 'dont_merge' | null;
}

/** A row the merge deliberately left where it was. */
export interface MergeSkip {
  op: 'skip';
  step: string;
  table: string;
  pk?: number | string;
  field?: string;
  label?: string;
  reason: string;
  constraint?: string | null;
}

export interface DuplicateMergeResult {
  success: boolean;
  dry_run: boolean;
  survivor_id: number;
  /** Per duplicate id, a per-table count of what moved. */
  merged: Record<string, Record<string, number>>;
  /** The journal row a revert uses; null on a dry run. */
  journal_id?: number | null;
  cases?: MergeCase[];
  fields_changed?: { field: string; label: string; source: string; member_id: number }[];
  skipped?: MergeSkip[];
}

/** Where a field's merged value comes from (TRUS-558). */
export type MergeFieldSource = 'survivor' | 'duplicate' | 'chosen' | 'not_merged' | 'both_blank';

export interface MergePreviewField {
  field: string;
  label: string;
  /** backfill: a blank survivor is filled from a duplicate; survivor_only: never merged. */
  mode: 'backfill' | 'survivor_only';
  survivor_value: unknown;
  /** Blank, whitespace or a placeholder such as "n/a". */
  survivor_blank?: boolean;
  duplicate_values: { member_id: number; value: unknown; blank?: boolean }[];
  merged_value: unknown;
  source: MergeFieldSource;
  source_member_id: number | null;
  /** The merged value differs from a value another record holds. */
  conflict: boolean;
  /** Some duplicate value does not reach the merged profile. */
  dropped: boolean;
  /** Back-fill field where the records hold 2+ different real values: pick the winner. */
  choosable?: boolean;
}

export interface DuplicateMergePreview {
  success: boolean;
  survivor_id: number;
  duplicate_ids: number[];
  members: Record<string, { id: number; first_name: string | null; last_name: string | null; member_number: string | null }>;
  fields: MergePreviewField[];
  summary: {
    conflicts: string[];
    filled_from_duplicate: string[];
    chosen?: string[];
    not_carried_over: string[];
  };
  /** Per duplicate id, per-table counts of content that would move; null if unavailable. */
  moved: Record<string, Record<string, number>> | null;
  /** Values treated as blank ("n/a", "unknown", ...). */
  placeholder_values?: string[];
  cases?: MergeCase[];
  cases_missing_decision?: number[];
  /** Cases marked dont_merge in the decisions the preview was given. */
  cases_declined?: number[];
}

export interface RevertConflict {
  table: string;
  pk: number | string;
  reason: 'changed_since_merge' | 'row_deleted_since_merge';
  step?: string;
  field?: string;
  columns: Record<string, { before_merge: unknown; merge_wrote: unknown; current: unknown }>;
}

export interface RevertReport {
  restored: Record<string, number>;
  removed: Record<string, number>;
  conflicts: RevertConflict[];
}

export interface MergeHistoryEntry {
  id: number;
  survivor_member_id: number;
  duplicate_member_ids: number[];
  merged_by: number | null;
  merged_by_name: string | null;
  merged_at: string | null;
  match_reason: string | null;
  notes: string | null;
  field_choices: Record<string, number>;
  case_decisions: Record<string, CaseDecision>;
  summary: {
    moved?: Record<string, Record<string, number>>;
    fields?: { field: string; label: string; source: string; member_id: number }[];
    cases?: { enrollment_id: number; program_name: string | null; action: string | null; survivor_enrollment_id: number | null }[];
    counts?: { changed: Record<string, Record<string, number>>; skipped: Record<string, number> };
  };
  reverted_at: string | null;
  reverted_by: number | null;
  reverted_by_name: string | null;
  revert_report: RevertReport | null;
  members: Record<string, { id: number; first_name?: string | null; last_name?: string | null; member_number?: string | null }>;
}

export interface MergeHistoryResponse {
  cbo_id: number;
  total: number;
  merges: MergeHistoryEntry[];
}

/** One "different people" dismissal of a duplicate group. */
export interface DuplicateDismissal {
  /** Resolution id to pass to undoDuplicateDismissal. */
  id: number;
  resolved_at: string | null;
  resolved_by: number | null;
  resolved_by_name: string | null;
  match_reason: string | null;
  notes: string | null;
  member_ids: number[];
  members: Record<string, { id: number; first_name?: string | null; last_name?: string | null; member_number?: string | null }>;
}

export interface MergeRevertResult {
  success: boolean;
  journal_id: number;
  survivor_id: number;
  duplicate_ids: number[];
  revert_report: RevertReport;
}

export const membersAPI = new MembersAPI();

// ============================================================================
// REACT HOOKS
// ============================================================================

export { default as useMembers } from '@/hooks/useMembers';
export { default as useCustomFields } from '@/hooks/useCustomFields';
